import { ApiClient } from '@api/api-configuration';
import {
  BillingEntityTypeEnum,
  type BalancesListResponse,
  type BalancesSummaryResponse,
} from '@api/READ_ONLY/billing_api/Api';
import { ArrayOfAvailableSupportedCurrencies, DEFAULT_CURRENCY } from '@app/common/constants';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import dayjs from '@app/utils/dayjs-setup';
import type { SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { BalanceRow, BalanceTabKey } from './types';
import { DEFAULT_BALANCES_SORT } from './types';

const BILLING_ENTITY_TYPES = new Set(Object.values(BillingEntityTypeEnum));

const formatDateTime = (date: string | null | undefined) => {
  if (!date) return '-';

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) {
    return date;
  }

  return parsedDate.format('YYYY-MM-DD HH:mm');
};

const normalizeBalanceTabParam = (value: string | null): BalanceTabKey | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === BillingEntityTypeEnum.Client || normalized === 'clients') {
    return BillingEntityTypeEnum.Client;
  }

  if (normalized === BillingEntityTypeEnum.Doctor || normalized === 'doctors') {
    return BillingEntityTypeEnum.Doctor;
  }

  if (normalized === BillingEntityTypeEnum.Caregiver || normalized === 'caregiver' || normalized === 'caregivers') {
    return BillingEntityTypeEnum.Caregiver;
  }

  return BILLING_ENTITY_TYPES.has(normalized as BillingEntityTypeEnum) ? (normalized as BalanceTabKey) : null;
};

export const useBalances = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const rawTabParam = searchParams.get('tab');
  const rawTypeParam = searchParams.get('type');
  const initialTabParam = rawTabParam ?? rawTypeParam;
  const activeTab = normalizeBalanceTabParam(initialTabParam) ?? BillingEntityTypeEnum.Client;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sort, setSort] = useState(DEFAULT_BALANCES_SORT);

  const resolvedSearchTerm = debouncedSearchTerm.trim();
  const isSearchTooShort = resolvedSearchTerm.length > 0 && resolvedSearchTerm.length < 3;
  const querySearchTerm = resolvedSearchTerm.length >= 3 ? resolvedSearchTerm : undefined;
  const offset = (currentPage - 1) * pageSize;
  const entityType = activeTab;

  const balancesSummaryQuery = useQuery<BalancesSummaryResponse>({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_SUMMARY],
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getBalancesSummary();
      return response.data;
    },
  });

  const balancesListQuery = useQuery<BalancesListResponse>({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_LIST, entityType, querySearchTerm ?? '', sort, offset, pageSize],
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.listBalances({
        type: entityType,
        search: querySearchTerm,
        sort,
        offset,
        limit: pageSize,
      });

      return response.data;
    },
    enabled: !isSearchTooShort,
    // Keep showing the current rows while a sort/page/search refetch is in flight
    // (so it doesn't fall back to the initial-load skeleton on every sort).
    placeholderData: keepPreviousData,
  });

  const handleSetActiveTab = useCallback(
    (nextTab: BalanceTabKey) => {
      if (nextTab === activeTab) {
        return;
      }

      const nextSearchParams = new URLSearchParams(searchParamsKey);
      nextSearchParams.set('tab', nextTab);
      nextSearchParams.delete('type');
      setSearchParams(nextSearchParams, { replace: true });
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setCurrentPage(1);
    },
    [activeTab, searchParamsKey, setSearchParams]
  );

  const rows = useMemo<BalanceRow[]>(
    () =>
      (balancesListQuery.data?.data ?? []).map((item) => ({
        id: item.billingAccountId,
        key: `${item.billingAccountId}`,
        entityId: item.entityId,
        entityType,
        name: item.entityName,
        transaction: formatDateTime(item.lastMovement),
        balance: item.balance ?? 0,
        createdAt: item.createdAt,
      })),
    [balancesListQuery.data?.data, entityType]
  );

  const totalCount = balancesListQuery.data?.total ?? 0;
  const rangeStart = totalCount ? offset + 1 : 0;
  const rangeEnd = totalCount ? Math.min(offset + pageSize, totalCount) : 0;

  // Only party types (client/doctor/caregiver) have summary totals; the
  // balances-summary endpoint never returns a caregiver_cash_register total.
  const totalsByEntityType = useMemo<Partial<Record<BillingEntityTypeEnum, number>>>(
    () => ({
      [BillingEntityTypeEnum.Client]: balancesSummaryQuery.data?.totals?.clients ?? 0,
      [BillingEntityTypeEnum.Doctor]: balancesSummaryQuery.data?.totals?.doctors ?? 0,
      [BillingEntityTypeEnum.Caregiver]: balancesSummaryQuery.data?.totals?.caregivers ?? 0,
    }),
    [
      balancesSummaryQuery.data?.totals?.caregivers,
      balancesSummaryQuery.data?.totals?.clients,
      balancesSummaryQuery.data?.totals?.doctors,
    ]
  );

  const responseCurrency = balancesSummaryQuery.data?.currency;

  const currency: SupportedCurrencies =
    responseCurrency && ArrayOfAvailableSupportedCurrencies.includes(responseCurrency as SupportedCurrencies)
      ? (responseCurrency as SupportedCurrencies)
      : DEFAULT_CURRENCY;

  useEffect(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm.length === 0) {
      setDebouncedSearchTerm('');
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(normalizedSearchTerm);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (rawTabParam === activeTab && rawTypeParam === null) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParamsKey);
    nextSearchParams.set('tab', activeTab);
    nextSearchParams.delete('type');
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeTab, rawTabParam, rawTypeParam, searchParamsKey, setSearchParams]);

  return {
    activeTab,
    currentPage,
    pageSize,
    searchTerm,
    sort,
    rows,
    totalCount,
    rangeStart,
    rangeEnd,
    totalsByEntityType,
    currency,
    // Skeleton only on the very first load; sort/page/search refetches keep the
    // current rows and show the table's loading bar instead (see isListFetching).
    isListLoading: balancesListQuery.isLoading,
    isListFetching: balancesListQuery.isFetching,
    isListError: balancesListQuery.isError,
    setActiveTab: handleSetActiveTab,
    setSearchTerm,
    setSort,
    setCurrentPage,
    setPageSize: (nextSize: number) => {
      if (nextSize !== pageSize) {
        setPageSize(nextSize);
        setCurrentPage(1);
      }
    },
  };
};
